import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Printer, Calendar, MapPin, User, Phone } from 'lucide-react';

const RoadmapView = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoadmap();
  }, [id]);

  // Add print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page {
          size: A4;
          margin: 12mm;
        }
        
        body {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        
        .no-print {
          display: none !important;
        }
        
        /* Reduce padding for print */
        .p-8, .p-6 {
          padding: 12px !important;
        }
        
        .py-8, .py-6 {
          padding-top: 12px !important;
          padding-bottom: 12px !important;
        }
        
        /* Reduce spacing */
        .space-y-6 > * + * {
          margin-top: 12px !important;
        }
        
        .space-y-4 > * + * {
          margin-top: 8px !important;
        }
        
        /* Avoid page breaks in blocks */
        .border-l-4, .border {
          page-break-inside: avoid;
        }
        
        h1 { font-size: 18px !important; }
        h2 { font-size: 15px !important; }
        h3 { font-size: 14px !important; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const loadRoadmap = async () => {
    try {
      const res = await axios.get(`${API}/roadmaps/${id}`, { withCredentials: true });
      if (res.data.error) {
        console.error('Roadmap error:', res.data.error);
        setData(null);
      } else {
        setData(res.data);
      }
    } catch (error) {
      console.error('Error loading roadmap:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p>Feuille de route introuvable</p>
      </div>
    );
  }

  const { roadmap, devis } = data;

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4">
      {/* Actions bar */}
      <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between no-print">
        <Link to="/" className="flex items-center gap-2 text-stone-600 hover:text-stone-800">
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Link>
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Imprimer
        </Button>
      </div>

      {/* Document */}
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none print:rounded-none">
        {/* Header */}
        <div className="p-8 border-b bg-stone-800 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-bold text-amber-400">KOLNOA PRODUCTION</h2>
              <p className="text-sm text-stone-300 mt-1">Photo & Vidéo Événementielle</p>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-bold">FEUILLE DE ROUTE</h1>
              <p className="text-stone-300">PRESTATAIRES</p>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-semibold">{roadmap.event_title}</h2>
            <p className="text-amber-400 flex items-center gap-2 mt-2">
              <Calendar className="h-4 w-4" />
              {roadmap.event_date}
            </p>
          </div>
        </div>

        {/* Client Info */}
        {devis && (
          <div className="p-6 bg-stone-50 border-b">
            <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              CLIENTS
            </h3>
            <p className="font-medium">{devis.client_info?.prenom} {devis.client_info?.nom}</p>
            <p className="text-sm text-stone-600 flex items-center gap-2">
              <Phone className="h-3 w-3" />
              {devis.client_info?.telephone}
            </p>
          </div>
        )}

        {/* Blocks */}
        <div className="p-8 space-y-8">
          {roadmap.blocks?.length > 0 ? (
            roadmap.blocks.map((block, i) => (
              <div key={i} className="border rounded-xl overflow-hidden">
                <div className="bg-amber-600 text-white p-4">
                  <h3 className="font-bold text-lg">{block.title}</h3>
                  <p className="text-amber-100 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(block.date)} {block.heure && `à ${block.heure}`}
                  </p>
                </div>
                <div className="p-4 space-y-3">
                  {block.lieu && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-stone-400 mt-1" />
                      <div>
                        <p className="font-medium">{block.lieu}</p>
                        <p className="text-sm text-stone-600">{block.adresse}</p>
                      </div>
                    </div>
                  )}
                  {block.prestataires?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-semibold text-stone-500 mb-2">ÉQUIPE ASSIGNÉE</p>
                      <div className="grid grid-cols-2 gap-2">
                        {block.prestataires.map((p, j) => {
                          const roleLabels = {
                            'videaste_principal': 'Vidéaste Principal',
                            'videaste_2': 'Vidéaste 2',
                            'photographe_principal': 'Photographe Principal',
                            'photographe_2': 'Photographe 2',
                            'drone': 'Drone',
                            'monteur': 'Monteur',
                            'autre': 'Autre'
                          };
                          return (
                            <div key={j} className="flex items-center gap-2 p-2 bg-stone-50 rounded">
                              <User className="h-4 w-4 text-amber-600" />
                              <div>
                                <p className="font-medium text-sm">{p.name || 'À assigner'}</p>
                                <p className="text-xs text-stone-500">{roleLabels[p.role] || p.role}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {block.notes && (
                    <p className="text-sm text-stone-600 italic mt-2">{block.notes}</p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-stone-500 py-8">Aucun bloc défini pour cette feuille de route.</p>
          )}

          {/* Options */}
          {devis?.lines?.some(l => l.prestation_name.includes('Magnets') || l.prestation_name.includes('Drone')) && (
            <div className="border rounded-xl p-4">
              <h3 className="font-semibold text-stone-800 mb-3">LOGISTIQUE SOIRÉE</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {devis.lines.filter(l => l.prestation_name.includes('Magnets')).map((l, i) => (
                  <div key={i} className="p-3 bg-amber-50 rounded-lg">
                    <p className="font-medium">MAGNETS</p>
                    <p className="text-sm text-stone-600">{l.prestation_name}</p>
                  </div>
                ))}
                {devis.lines.filter(l => l.prestation_name.includes('Drone')).map((l, i) => (
                  <div key={i} className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium">DRONE</p>
                    <p className="text-sm text-stone-600">{l.prestation_name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {roadmap.notes && (
            <div className="border rounded-xl p-4">
              <h3 className="font-semibold text-stone-800 mb-2">NOTES & DÉTAILS</h3>
              <p className="text-stone-600">{roadmap.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-stone-800 text-white text-center">
          <p className="text-sm">KOLNOA PRODUCTION</p>
          <p className="text-xs text-stone-400">06.68.11.93.43 | kolnoaprod@gmail.com</p>
        </div>
      </div>
    </div>
  );
};

export default RoadmapView;
